import { useCallback, useEffect, useState } from "react";
import { formatCurrency, formatMonthLabel, formatNumber } from "@/components/deal-metrics/shared";
import { formatLongMonthLabel } from "@/components/executive/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { currentFiscalYear, formatFY } from "@/lib/fiscal";
import { GOAL_TEXT, goalStatus } from "@/lib/goal-status";
import { cn } from "@/lib/utils";

interface CreatedMonthlyRow {
  month: string;
  popupUnits: number;
  webUnits: number;
  totalUnits: number;
  popupGrossSales: number;
  webGrossSales: number;
  totalGrossSales: number;
  instagramFollowers: number | null;
  websiteVisitors: number | null;
}

interface ApiResponse {
  fiscalYear: number;
  data: CreatedMonthlyRow[];
}

interface MetricRowDef {
  key: string;
  label: string;
  getValue: (row: CreatedMonthlyRow) => string;
  getNumber: (row: CreatedMonthlyRow) => number;
  format: (n: number) => string;
  aggregate: "sum";
}

const METRIC_ROWS: MetricRowDef[] = [
  {
    key: "instagramFollowers",
    label: "Created Instagram Followers",
    getValue: (r) => (r.instagramFollowers != null ? formatNumber(r.instagramFollowers) : "—"),
    getNumber: (r) => r.instagramFollowers ?? 0,
    aggregate: "sum",
    format: formatNumber,
  },
  {
    key: "websiteVisitors",
    label: "Created Website Visitors",
    getValue: (r) => (r.websiteVisitors != null ? formatNumber(r.websiteVisitors) : "—"),
    getNumber: (r) => r.websiteVisitors ?? 0,
    aggregate: "sum",
    format: formatNumber,
  },
  {
    key: "popupUnits",
    label: "Pop Up Orders",
    getValue: (r) => formatNumber(r.popupUnits),
    getNumber: (r) => r.popupUnits,
    aggregate: "sum",
    format: formatNumber,
  },
  {
    key: "webUnits",
    label: "Web Orders",
    getValue: (r) => formatNumber(r.webUnits),
    getNumber: (r) => r.webUnits,
    aggregate: "sum",
    format: formatNumber,
  },
  {
    key: "totalUnits",
    label: "Total Orders",
    getValue: (r) => formatNumber(r.totalUnits),
    getNumber: (r) => r.totalUnits,
    aggregate: "sum",
    format: formatNumber,
  },
  {
    key: "popupGrossSales",
    label: "Pop Up Gross Sales",
    getValue: (r) => formatCurrency(r.popupGrossSales),
    getNumber: (r) => r.popupGrossSales,
    aggregate: "sum",
    format: formatCurrency,
  },
  {
    key: "webGrossSales",
    label: "Web Gross Sales",
    getValue: (r) => formatCurrency(r.webGrossSales),
    getNumber: (r) => r.webGrossSales,
    aggregate: "sum",
    format: formatCurrency,
  },
  {
    key: "totalGrossSales",
    label: "Total Gross Sales",
    getValue: (r) => formatCurrency(r.totalGrossSales),
    getNumber: (r) => r.totalGrossSales,
    aggregate: "sum",
    format: formatCurrency,
  },
];

// ── Monthly goals (per-month targets for the fiscal year) ─────────────

interface CreatedGoal {
  value: number;
  comparison: "gte" | "lte";
  format: "count" | "currency";
}

type MonthGoals = Partial<Record<string, CreatedGoal>>;

// Goals keyed by "YYYY-MM". Only totalUnits and totalGrossSales have targets
// for now — pop-up vs web split goals can be added per month as needed.
const GOALS_BY_MONTH: Record<string, MonthGoals> = {
  "2026-06": {
    totalUnits: { value: 254, comparison: "gte", format: "count" },
    totalGrossSales: { value: 29700.0, comparison: "gte", format: "currency" },
  },
  "2026-07": {
    totalUnits: { value: 212, comparison: "gte", format: "count" },
    totalGrossSales: { value: 26519.2, comparison: "gte", format: "currency" },
  },
  "2026-08": {
    totalUnits: { value: 428, comparison: "gte", format: "count" },
    totalGrossSales: { value: 53543.86, comparison: "gte", format: "currency" },
  },
  "2026-09": {
    totalUnits: { value: 159, comparison: "gte", format: "count" },
    totalGrossSales: { value: 19936.84, comparison: "gte", format: "currency" },
  },
  "2026-10": {
    totalUnits: { value: 217, comparison: "gte", format: "count" },
    totalGrossSales: { value: 27162.58, comparison: "gte", format: "currency" },
  },
  "2026-11": {
    totalUnits: { value: 789, comparison: "gte", format: "count" },
    totalGrossSales: { value: 98626.72, comparison: "gte", format: "currency" },
  },
  "2026-12": {
    totalUnits: { value: 914, comparison: "gte", format: "count" },
    totalGrossSales: { value: 114210.7, comparison: "gte", format: "currency" },
  },
  "2027-01": {
    totalUnits: { value: 150, comparison: "gte", format: "count" },
    totalGrossSales: { value: 18750.0, comparison: "gte", format: "currency" },
  },
  "2027-02": {
    totalUnits: { value: 240, comparison: "gte", format: "count" },
    totalGrossSales: { value: 30000.0, comparison: "gte", format: "currency" },
  },
  "2027-03": {
    totalUnits: { value: 210, comparison: "gte", format: "count" },
    totalGrossSales: { value: 26250.0, comparison: "gte", format: "currency" },
  },
};

function fmtGoal(value: number, format: CreatedGoal["format"]): string {
  return format === "currency" ? formatCurrency(value) : formatNumber(Math.round(value));
}

// ── Current month performance card ────────────────────────────────────

function daysInMonth(): { elapsed: number; total: number } {
  const now = new Date();
  const total = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return { elapsed: now.getDate(), total };
}

const CURRENT_MONTH_METRICS: {
  key: keyof CreatedMonthlyRow;
  label: string;
  format: CreatedGoal["format"];
  nullable?: boolean;
}[] = [
  {
    key: "instagramFollowers",
    label: "Created Instagram Followers",
    format: "count",
    nullable: true,
  },
  { key: "websiteVisitors", label: "Created Website Visitors", format: "count", nullable: true },
  { key: "totalUnits", label: "Total Orders", format: "count" },
  { key: "totalGrossSales", label: "Total Gross Sales", format: "currency" },
];

function CurrentMonthCard({ row }: { row: CreatedMonthlyRow }) {
  const { elapsed, total } = daysInMonth();
  const scale = elapsed > 0 ? total / elapsed : 1;
  const monthGoals: MonthGoals = GOALS_BY_MONTH[row.month] ?? {};

  return (
    <Card>
      <CardHeader>
        <CardTitle>Current Month Performance</CardTitle>
        <p className="text-sm text-muted-foreground">
          {formatLongMonthLabel(row.month)} — Created Team · day {elapsed} of {total}
        </p>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Metric</TableHead>
              <TableHead className="text-right whitespace-nowrap font-semibold">
                This Month
              </TableHead>
              <TableHead className="text-right whitespace-nowrap font-semibold">
                Projected (Month-End)
              </TableHead>
              <TableHead className="text-right whitespace-nowrap font-semibold">
                Monthly Goal
              </TableHead>
              <TableHead className="text-right whitespace-nowrap font-semibold">
                Remaining to Goal
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {CURRENT_MONTH_METRICS.map(({ key, label, format, nullable }) => {
              const raw = row[key] as number | null;
              const isNull = nullable && raw == null;
              const value = raw ?? 0;
              const projected = value * scale;
              const goal = monthGoals[key];
              const met =
                !isNull && goal
                  ? goal.comparison === "gte"
                    ? value >= goal.value
                    : value <= goal.value
                  : null;
              const projectedMet =
                !isNull && goal
                  ? goal.comparison === "gte"
                    ? projected >= goal.value
                    : projected <= goal.value
                  : null;
              const metStatus =
                isNull || !goal ? "none" : goalStatus(value, goal.value, goal.comparison);
              const projectedStatus =
                isNull || !goal ? "none" : goalStatus(projected, goal.value, goal.comparison);
              const remaining =
                !isNull && goal
                  ? goal.comparison === "gte"
                    ? Math.max(goal.value - value, 0)
                    : goal.value - value
                  : null;
              return (
                <TableRow key={key}>
                  <TableCell className="font-medium whitespace-nowrap">{label}</TableCell>
                  <TableCell className="text-right whitespace-nowrap font-semibold">
                    {isNull ? "—" : fmtGoal(value, format)}
                  </TableCell>
                  <TableCell
                    className={cn("text-right whitespace-nowrap", GOAL_TEXT[projectedStatus])}
                  >
                    {isNull ? "—" : fmtGoal(projected, format)}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap text-muted-foreground">
                    {goal ? fmtGoal(goal.value, goal.format) : "—"}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right whitespace-nowrap font-semibold",
                      GOAL_TEXT[metStatus],
                    )}
                  >
                    {remaining === null
                      ? "—"
                      : met
                        ? "Goal met"
                        : fmtGoal(remaining, goal?.format ?? format)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function rowTotal(def: MetricRowDef, rows: CreatedMonthlyRow[]): string {
  const sum = rows.reduce((acc, r) => acc + def.getNumber(r), 0);
  return def.format(sum);
}

export function CreatedDevelopmentMonthlyTable() {
  const availableFYs = [currentFiscalYear(), currentFiscalYear() - 1];
  const [fiscalYear, setFiscalYear] = useState(currentFiscalYear());
  const [rows, setRows] = useState<CreatedMonthlyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async (fy: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/created-development/monthly-metrics?fy=${fy}`);
      if (!res.ok) throw new Error("Failed to load data");
      const json: ApiResponse = await res.json();
      setRows(json.data);
    } catch {
      setError("Could not load Created team data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(fiscalYear);
  }, [fiscalYear, load]);

  const syncVisitors = useCallback(async () => {
    setSyncing(true);
    try {
      await fetch("/api/created-development/sync-website-visitors", { method: "POST" });
      await load(fiscalYear);
    } finally {
      setSyncing(false);
    }
  }, [fiscalYear, load]);

  const now = new Date();
  const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const currentMonthRow = rows.find((r) => r.month === currentMonthKey);

  return (
    <div className="mt-8 space-y-6">
      {!loading && currentMonthRow && <CurrentMonthCard row={currentMonthRow} />}

      <div className="flex items-center justify-between">
        <h2 className="font-serif text-lg font-semibold tracking-wide uppercase text-foreground">
          Created Monthly Performance
        </h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={syncVisitors} disabled={syncing}>
            {syncing ? "Syncing..." : "Sync Website Visitors"}
          </Button>
          {availableFYs.map((fy) => (
            <Button
              key={fy}
              size="sm"
              variant={fy === fiscalYear ? "default" : "outline"}
              onClick={() => setFiscalYear(fy)}
            >
              {formatFY(fy)}
            </Button>
          ))}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[180px] bg-muted/50 font-semibold">Metric</TableHead>
              {loading
                ? Array.from({ length: 12 }).map((_, i) => (
                    <TableHead key={i} className="text-center">
                      <Skeleton className="h-4 w-10 mx-auto" />
                    </TableHead>
                  ))
                : rows.map((r) => (
                    <TableHead key={r.month} className="text-center text-xs">
                      {formatMonthLabel(r.month)}
                    </TableHead>
                  ))}
              <TableHead className="text-center bg-muted/50 font-semibold text-xs">TOTAL</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {METRIC_ROWS.map((def) => (
              <TableRow key={def.key}>
                <TableCell className="font-medium text-sm bg-muted/20">{def.label}</TableCell>
                {loading
                  ? Array.from({ length: 12 }).map((_, i) => (
                      <TableCell key={i} className="text-center">
                        <Skeleton className="h-4 w-14 mx-auto" />
                      </TableCell>
                    ))
                  : rows.map((r) => (
                      <TableCell
                        key={r.month}
                        className={cn(
                          "text-center text-sm tabular-nums",
                          def.getNumber(r) === 0 && "text-muted-foreground",
                        )}
                      >
                        {def.getValue(r)}
                      </TableCell>
                    ))}
                <TableCell className="text-center text-sm font-semibold tabular-nums bg-muted/20">
                  {loading ? <Skeleton className="h-4 w-14 mx-auto" /> : rowTotal(def, rows)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
